import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Checkbox,
  alpha,
} from '@mui/material';
import {
  DateRange,
  EventBusy,
} from '@mui/icons-material';
import { AppLayout } from '@/components/layout';
import { useAggregatedTasks, formatDayHeader, type AggregatedTask } from '@/hooks';
import { todolistsService } from '@/services/collections/todolists';
import { brandColors } from '@/theme/colors';
import { PRIORITY_COLORS } from '@/types/todolist';

export const UpcomingPage = () => {
  const navigate = useNavigate();
  const { groupedByDay, loading, upcomingCount } = useAggregatedTasks('upcoming');

  const handleToggleTask = async (task: AggregatedTask) => {
    await todolistsService.updateItem(task.listId, task.id, {
      completed: !task.completed,
    });
  };

  const handleTaskClick = (task: AggregatedTask) => {
    navigate(`/todo/${task.listId}`);
  };

  if (loading) {
    return (
      <AppLayout showBackToDashboard backToSection="todolists" title="Upcoming">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  const dayEntries = groupedByDay ? Array.from(groupedByDay.entries()) : [];
  const hasNoTasks = upcomingCount === 0;

  return (
    <AppLayout showBackToDashboard backToSection="todolists" title="Upcoming">
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <DateRange sx={{ fontSize: 32, color: brandColors.purple }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: brandColors.purple }}>
              Upcoming
            </Typography>
            {upcomingCount > 0 && (
              <Chip
                label={upcomingCount}
                size="small"
                sx={{
                  bgcolor: brandColors.purple,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            Tasks for the next 7 days
          </Typography>
        </Box>

        {hasNoTasks ? (
          <Paper
            sx={{
              textAlign: 'center',
              py: 8,
              bgcolor: alpha(brandColors.purple, 0.02),
              border: '1px dashed',
              borderColor: alpha(brandColors.purple, 0.2),
              borderRadius: 2,
            }}
          >
            <EventBusy sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No upcoming tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You have no tasks scheduled for the next 7 days.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {dayEntries.map(([dateKey, tasks]) => (
              <DaySection
                key={dateKey}
                dateKey={dateKey}
                tasks={tasks}
                onToggleTask={handleToggleTask}
                onTaskClick={handleTaskClick}
              />
            ))}
          </Box>
        )}
      </Container>
    </AppLayout>
  );
};

// Day section component
const DaySection = ({
  dateKey,
  tasks,
  onToggleTask,
  onTaskClick,
}: {
  dateKey: string;
  tasks: AggregatedTask[];
  onToggleTask: (task: AggregatedTask) => void;
  onTaskClick: (task: AggregatedTask) => void;
}) => {
  const dayHeader = formatDayHeader(dateKey);
  const isToday = dayHeader === 'Today';
  const isEmpty = tasks.length === 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: isToday ? brandColors.purple : 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: isToday ? brandColors.purple : 'text.primary',
          }}
        >
          {dayHeader}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
        </Typography>
      </Box>

      {isEmpty ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, fontStyle: 'italic' }}
        >
          No tasks scheduled
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {tasks.map((task) => (
            <TaskRow
              key={`${task.listId}-${task.id}`}
              task={task}
              onToggle={onToggleTask}
              onClick={onTaskClick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Task row component
const TaskRow = ({
  task,
  onToggle,
  onClick,
}: {
  task: AggregatedTask;
  onToggle: (task: AggregatedTask) => void;
  onClick: (task: AggregatedTask) => void;
}) => {
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(task);
  };

  // Extract time if present
  const dueTime = task.dueDate?.toDate();
  const hasTime = dueTime && (dueTime.getHours() !== 0 || dueTime.getMinutes() !== 0);
  const timeString = hasTime
    ? dueTime.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', hour12: true })
    : null;

  return (
    <Paper
      onClick={() => onClick(task)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: brandColors.purple,
          boxShadow: 1,
        },
      }}
    >
      <Checkbox
        checked={task.completed}
        onClick={handleCheckboxClick}
        sx={{
          color: task.priority ? PRIORITY_COLORS[task.priority] : brandColors.purple,
          '&.Mui-checked': {
            color: brandColors.accent.green,
          },
        }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0, mx: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            textDecoration: task.completed ? 'line-through' : 'none',
            color: task.completed ? 'text.disabled' : 'text.primary',
          }}
        >
          {task.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          {timeString && (
            <Chip
              size="small"
              label={timeString}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: alpha(brandColors.purple, 0.1),
                color: brandColors.purple,
              }}
            />
          )}
          {task.priority && (
            <Chip
              size="small"
              label={task.priority}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: alpha(PRIORITY_COLORS[task.priority], 0.1),
                color: PRIORITY_COLORS[task.priority],
                textTransform: 'capitalize',
              }}
            />
          )}
        </Box>
      </Box>

      <Chip
        size="small"
        label={task.listName}
        variant="outlined"
        sx={{
          height: 24,
          fontSize: '0.7rem',
          borderColor: alpha(brandColors.purple, 0.3),
          color: 'text.secondary',
        }}
      />
    </Paper>
  );
};
